/**
 * Section 4: Property Condition + Details
 */

import { useCallback } from 'preact/hooks';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { Select, Textarea } from '@ox/visits-ui/components/shared/FormComponents';
import { FileUpload } from '@ox/visits-ui/components/shared/FileUpload';

interface Section4ExteriorConditionProps {
  /** Combined (QC Standard) variant: hides the per-item description fields and allows more damage photos. */
  combined?: boolean;
}

export function Section4ExteriorCondition({ combined }: Section4ExteriorConditionProps) {
  const { formData, updateField } = useForm();

  const handleRoofConditionChange = useCallback((value: string) => {
    updateField('roof_condition', value);
  }, [updateField]);

  const handleRoofConditionDescriptionChange = useCallback((value: string) => {
    updateField('roof_condition_description', value);
  }, [updateField]);

  const handleExteriorPaintChange = useCallback((value: string) => {
    updateField('exterior_paint', value);
  }, [updateField]);

  const handleExteriorPaintDescriptionChange = useCallback((value: string) => {
    updateField('exterior_paint_description', value);
  }, [updateField]);

  const handleLandscapingChange = useCallback((value: string) => {
    updateField('landscaping', value);
  }, [updateField]);

  const handleLandscapingDescriptionChange = useCallback((value: string) => {
    updateField('landscaping_description', value);
  }, [updateField]);

  const handleSignStatusChange = useCallback((value: string) => {
    updateField('sign_status', value);
  }, [updateField]);

  const handleSignDescriptionChange = useCallback((value: string) => {
    updateField('sign_description', value);
  }, [updateField]);

  const handleVisibleDamageChange = useCallback((value: string) => {
    updateField('visible_damage', value);
  }, [updateField]);

  const handleMailOrNoticesChange = useCallback((value: string) => {
    updateField('mail_or_notices', value);
  }, [updateField]);

  return (
    <div className="form-section">
      <h2>Exterior Condition</h2>

      <Select
        label="Roof Condition Assessment"
        name="roof_condition"
        value={
          formData.roof_condition || ''
        }
        onChange={handleRoofConditionChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Good', value: 'Good' },
          { label: 'Poor', value: 'Poor' },
        ]}
        required
      />
      {!combined && (formData.roof_condition === 'Poor' || formData.roof_condition === 'Fair') && (
        <Textarea
          label="Describe the roof condition issues"
          name="roof_condition_description"
          value={formData.roof_condition_description || ''}
          onChange={handleRoofConditionDescriptionChange}
          placeholder="Describe the roof condition issues"
          autoSize
          required
        />
      )}
      {combined && formData.roof_condition === 'Poor' && (
        <>
          <Textarea
            label="Describe the roof condition issues"
            name="roof_condition_description"
            value={formData.roof_condition_description || ''}
            onChange={handleRoofConditionDescriptionChange}
            autoSize
            required
          />
          <FileUpload
            label="Photo of the roof condition issue(s)"
            mediaType="photo_roof_condition"
            sectionNumber={5}
            minFiles={1}
            maxFiles={10}
            accept="image/*"
            required
          />
        </>
      )}

      <Select
        label="Exterior Paint/Siding Condition"
        name="exterior_paint"
        value={
          formData.exterior_paint || ''
        }
        onChange={handleExteriorPaintChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Good', value: 'Good' },
          { label: 'Poor', value: 'Poor' },
        ]}
        required
      />
      {!combined && (formData.exterior_paint === 'Poor' || formData.exterior_paint === 'Fair') && (
        <Textarea
          label="Describe the exterior paint/siding condition issues"
          name="exterior_paint_description"
          value={formData.exterior_paint_description || ''}
          onChange={handleExteriorPaintDescriptionChange}
          placeholder="Describe the exterior paint/siding condition issues"
          autoSize
          required
        />
      )}
      {combined && formData.exterior_paint === 'Poor' && (
        <>
          <Textarea
            label="Describe the exterior paint/siding condition issues"
            name="exterior_paint_description"
            value={formData.exterior_paint_description || ''}
            onChange={handleExteriorPaintDescriptionChange}
            autoSize
            required
          />
          <FileUpload
            label="Photo of the exterior paint/siding issue(s)"
            mediaType="photo_exterior_paint"
            sectionNumber={5}
            minFiles={1}
            maxFiles={10}
            accept="image/*"
            required
          />
        </>
      )}

      <Select
        label="Landscaping/Yard Maintenance"
        name="landscaping"
        value={
          formData.landscaping || ''
        }
        onChange={handleLandscapingChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Good', value: 'Good' },
          { label: 'Poor', value: 'Poor' },
        ]}
        required
      />
      {!combined && (formData.landscaping === 'Poor' || formData.landscaping === 'Fair') && (
        <Textarea
          label="Describe the landscaping/yard maintenance issues"
          name="landscaping_description"
          value={formData.landscaping_description || ''}
          onChange={handleLandscapingDescriptionChange}
          placeholder="Describe the landscaping/yard maintenance issues"
          autoSize
          required
        />
      )}
      {combined && formData.landscaping === 'Poor' && (
        <>
          <Textarea
            label="Describe the landscaping/yard maintenance issues"
            name="landscaping_description"
            value={formData.landscaping_description || ''}
            onChange={handleLandscapingDescriptionChange}
            autoSize
            required
          />
          <FileUpload
            label="Photo of the landscaping/yard issue(s)"
            mediaType="photo_landscaping"
            sectionNumber={5}
            minFiles={1}
            maxFiles={10}
            accept="image/*"
            required
          />
        </>
      )}

      <Select
        label="Does the home have a valid sign installed?"
        name="sign_status"
        value={
          formData.sign_status || ''
        }
        onChange={handleSignStatusChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />
      {!combined && (formData.sign_status === 'No') && (
        <Textarea
          label="Please describe any damage to the sign or post"
          name="sign_description"
          value={formData.sign_description || ''}
          onChange={handleSignDescriptionChange}
          placeholder="Describe the sign or post damage"
          autoSize
          required
        />
      )}

      <Textarea
        label="Is there any specific exterior conditions/detractors to the home that you would you like to note?"
        name="visible_damage"
        value={formData.visible_damage || ''}
        onChange={handleVisibleDamageChange}
        placeholder="Please provide location of damage with description. (ex. Mold found under first floor bathroom sink, photos to be provided in the next question)"
        autoSize
      />

      {combined && (
        <>
          <Select
            label="Is there any mail in the mailbox or notices posted in windows or on the property?"
            name="mail_or_notices"
            value={formData.mail_or_notices || ''}
            onChange={handleMailOrNoticesChange}
            options={[
              { label: 'Select...', value: '', disabled: true },
              { label: 'Yes', value: 'Yes' },
              { label: 'No', value: 'No' },
            ]}
            required
          />
          {formData.mail_or_notices === 'Yes' && (
            <FileUpload
              label="Photo of the mail or posted notices"
              mediaType="photo_mail_notices"
              sectionNumber={5}
              minFiles={1}
              maxFiles={10}
              accept="image/*"
              required
            />
          )}
        </>
      )}

      <FileUpload
        label={`Exterior Damage Photos (Up to ${combined ? 10 : 5} Photos)`}
        mediaType="photos_exterior_damage"
        sectionNumber={5}
        maxFiles={combined ? 10 : 5}
        accept="image/*"
      />
    </div>
  );
}
